import { ReactNode, useState, useEffect, useMemo, Suspense, lazy } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { SimpleFooter } from './SimpleFooter';
import { PerformanceIndicator } from '../ui/PerformanceIndicator';
import { useLocation } from 'react-router-dom';
import { FloatingComponentsProvider } from '../../context/FloatingComponentsContext';
import { usePerformance } from '../../context/PerformanceContext';
import { AdvancedLoadingScreen } from '../ui/AdvancedLoadingScreen';

// Lazy load des composants non-critiques pour améliorer le FCP
const FloatingLauncher = lazy(() => import('./FloatingLauncher').then(m => ({ default: m.FloatingLauncher })));

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { 
    isInitialLoading,
    registerPhase,
    completePhase,
  } = usePerformance();
  
  const [isFirstRender, setIsFirstRender] = useState(true);
  
  const isProfilePage = useMemo(() =>
    ['/profile', '/company', '/institution', '/abonnement'].includes(location.pathname) || 
    location.pathname.startsWith('/financial-institution'),
    [location.pathname]
  );

  // Enregistrer les phases de chargement
  useEffect(() => {
    registerPhase('Interface', 1);
    registerPhase('Données utilisateur', 2);
    registerPhase('Composants', 3);
  }, [registerPhase]);

  // Gérer le premier rendu
  useEffect(() => {
    if (isFirstRender) {
      const timer = setTimeout(() => {
        setIsFirstRender(false);
        completePhase('Interface');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isFirstRender, completePhase]);

  // Préchargement des ressources critiques
  useEffect(() => {
    const preloadCriticalResources = () => {
      const fontUrls = [
        'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap'
      ];
      
      fontUrls.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = url;
        link.as = 'style';
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      });
    };

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(preloadCriticalResources);
    } else {
      setTimeout(preloadCriticalResources, 100);
    }
  }, []);

  // Afficher l'écran de chargement initial UNIQUEMENT au tout premier chargement de l'app
  if (isInitialLoading || isFirstRender) {
    return (
      <AdvancedLoadingScreen 
        showConnectionStatus={true}
        showProgress={true}
        theme="brand"
        onComplete={() => completePhase('Composants')}
      />
    );
  }
  
  return (
    <FloatingComponentsProvider>
      <div className="min-h-screen flex flex-col relative overflow-x-hidden">
        <Header />
        
        <main className="flex-grow relative">
          {children}
        </main>
        
        {/* Indicateur de performance (dev mode uniquement) */}
        {process.env.NODE_ENV === 'development' && <PerformanceIndicator />}
        
        {/* Boutons flottants unifiés + panel chat */}
        <Suspense fallback={null}>
          <FloatingLauncher />
        </Suspense>
        
        {/* Footer - PAS de loader global ici, chaque page gère son skeleton inline */}
        {isProfilePage ? (
          <SimpleFooter className="relative z-10 w-full footer-container" />
        ) : (
          <Footer className="relative z-10 w-full footer-container" />
        )}
      </div>
    </FloatingComponentsProvider>
  );
}